import axiosClient from "../shared/api/axios-client";

/**
 * Single exercise from GET module-test-sessions/{sessionId}/exercises (ModuleExerciseDto).
 * No answer key; used for display with common multiple-choice component.
 */
export interface ModuleTestSessionExerciseDto {
  id: string;
  type: string;
  data: string; // JSON: question + options for multiple_choice
  answerData?: string; // JSON: e.g. { multipleResponses: boolean, correctOptionCount?: number }
  order?: number;
  moduleId?: string;
}

/**
 * Response of POST start module test.
 * Backend creates a new session and draws N questions from the pool.
 */
export interface StartModuleTestResponse {
  sessionId: string;
  exerciseIds: string[];
}

/**
 * Get exercises for a module test session (for UI; no answer key).
 * GET /students/module-test-sessions/{sessionId}/exercises
 */
export const apiGetModuleTestSessionExercises = async (
  sessionId: string
): Promise<ModuleTestSessionExerciseDto[]> => {
  const response = await axiosClient.get<ModuleTestSessionExerciseDto[]>(
    `/students/module-test-sessions/${sessionId}/exercises`
  );
  return response.data;
};

/**
 * Start module test: creates a new session and returns drawn exercise IDs in order.
 * POST /students/modules/{moduleId}/test/start
 * Backend validates module completed; returns session + ordered exercise list.
 */
export const apiStartModuleTest = async (
  moduleId: string
): Promise<StartModuleTestResponse> => {
  const response = await axiosClient.post<StartModuleTestResponse>(
    `/students/modules/${moduleId}/test/start`
  );
  return response.data;
};

// --- Submit answers & finish (add-module-test-submit-and-finish) ---

/**
 * One answer for the answers API (per question or batch).
 */
export interface ModuleTestAnswerItem {
  exerciseId: string;
  answer: unknown; // string, string[], or type-specific payload
}

/**
 * Submit one or more answers for the current module test session.
 * POST .../module-test-sessions/{sessionId}/answers
 * Backend marks IsCorrect per answer and persists.
 */
export const apiSubmitModuleTestAnswers = async (
  sessionId: string,
  answers: ModuleTestAnswerItem[]
): Promise<void> => {
  await axiosClient.post(
    `/students/module-test-sessions/${sessionId}/answers`,
    { answers }
  );
};

/** Módulo desbloqueado ao passar no teste (título vindo da API). */
export interface ModuleTestUnlockedModuleItem {
  moduleId: string;
  moduleTitle?: string | null;
}

/**
 * Result returned when finishing the module test.
 */
export interface FinishModuleTestResponse {
  scorePercent: number;
  passed: boolean;
  /** Vazio se reprovou, se não havia próximo módulo no pacote, ou ainda se a API for antiga. */
  unlockedModules?: ModuleTestUnlockedModuleItem[];
}

/**
 * Finish the module test session; backend computes score and Passed, persists, and unlocks next module if passed.
 * POST .../module-test-sessions/{sessionId}/finish
 */
export const apiFinishModuleTest = async (
  sessionId: string
): Promise<FinishModuleTestResponse> => {
  const response = await axiosClient.post<FinishModuleTestResponse>(
    `/students/module-test-sessions/${sessionId}/finish`
  );
  return response.data;
};
